import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link } from 'react-router-dom';
import { Hospital } from 'lucide-react';
import { reportError } from '../lib/errorReporter';

// Fix default marker icon paths (Leaflet bundling issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const ratingColor = (r) => {
  if (r >= 5) return '#2D5F4F';
  if (r >= 4) return '#4A7C5E';
  if (r >= 3) return '#D4A85C';
  if (r >= 2) return '#D58668';
  if (r >= 1) return '#C75D5D';
  return '#9A9077';
};

let _usStatesCache = null;
async function loadUSStatesGeo() {
  if (_usStatesCache) return _usStatesCache;
  const r = await fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json');
  _usStatesCache = await r.json();
  return _usStatesCache;
}

// Convert US state name (from GeoJSON) → 2-letter code
const STATE_NAME_TO_CODE = {
  'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO',
  'Connecticut':'CT','Delaware':'DE','District of Columbia':'DC','Florida':'FL','Georgia':'GA',
  'Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY',
  'Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN',
  'Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH',
  'New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND',
  'Ohio':'OH','Oklahoma':'OK','Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC',
  'South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA',
  'Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY',
};

export default function RealMap({ markers = [], height = 540, center = [39.5, -98.35], zoom = 4, heatmap = false, heatmapData = [], hospitals = null, hospitalLinks = null, showHospitalsToggle = false, defaultHospitalsOn = false }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const heatLayerRef = useRef(null);
  const hospitalLayerRef = useRef(null);
  const linksLayerRef = useRef(null);
  const [hospitalsOn, setHospitalsOn] = useState(defaultHospitalsOn || Boolean(hospitals?.length || hospitalLinks?.length));

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center,
      zoom,
      preferCanvas: true,
      scrollWheelZoom: true,
      zoomControl: true,
    });
    // OpenStreetMap tiles — free, no API key required.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!mapRef.current || !layerRef.current) return;
    layerRef.current.clearLayers();
    const bounds = [];
    markers.forEach((m) => {
      if (m.lat == null || m.lng == null) return;
      const color = ratingColor(m.rating || 0);
      const dot = L.circleMarker([m.lat, m.lng], {
        radius: 5,
        fillColor: color,
        color: '#FBF6EC',
        weight: 1.2,
        opacity: 1,
        fillOpacity: 0.95,
      });
      const popup = `
        <div style="font-family:Inter,sans-serif;font-size:12px;line-height:1.4;min-width:160px">
          <div style="font-weight:600;color:#1A2622">${(m.name || '').replace(/[<>]/g, '')}</div>
          <div style="color:#7A857F;margin-top:2px">${m.city || ''}${m.city && m.state ? ', ' : ''}${m.state || ''}</div>
          <div style="margin-top:6px;display:flex;align-items:center;gap:6px">
            <span style="display:inline-block;width:8px;height:8px;border-radius:99px;background:${color}"></span>
            <span style="color:#3A4A44">Rating: <b>${m.rating || 'N/A'}</b></span>
          </div>
          <a href="/facilities/${m.ccn}" style="display:inline-block;margin-top:8px;color:#2D5F4F;font-weight:600;text-decoration:none">View facility →</a>
        </div>`;
      dot.bindPopup(popup);
      dot.addTo(layerRef.current);
      bounds.push([m.lat, m.lng]);
    });
    if (bounds.length > 0 && bounds.length < 800) {
      try { mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 9 }); } catch (e) { reportError('RealMap.fitBounds', e); }
    }
  }, [markers]);

  // Hospital markers (acute-care hospital pins, blue cross icon)
  useEffect(() => {
    if (!mapRef.current) return;
    if (hospitalLayerRef.current) {
      mapRef.current.removeLayer(hospitalLayerRef.current);
      hospitalLayerRef.current = null;
    }
    if (!hospitalsOn || !hospitals || !hospitals.length) return;
    const group = L.layerGroup();
    hospitals.forEach((h) => {
      if (h.lat == null || h.lng == null) return;
      const rating = h.overall_rating || h.rating;
      const dot = L.circleMarker([h.lat, h.lng], {
        radius: 6,
        fillColor: '#34538F',
        color: '#FBF6EC',
        weight: 1.5,
        opacity: 1,
        fillOpacity: 0.95,
      });
      const popup = `
        <div style="font-family:Inter,sans-serif;font-size:12px;line-height:1.4;min-width:180px">
          <div style="display:inline-block;padding:1px 6px;border-radius:4px;background:#E0EAF8;color:#34538F;font-size:10px;font-weight:600;margin-bottom:4px">HOSPITAL</div>
          <div style="font-weight:600;color:#1A2622">${(h.name || '').replace(/[<>]/g, '')}</div>
          <div style="color:#7A857F;margin-top:2px">${h.city || ''}${h.city && h.state ? ', ' : ''}${h.state || ''}</div>
          ${rating ? `<div style="margin-top:4px;color:#A06628;font-weight:600">${rating}★</div>` : ''}
          <a href="/hospitals/${h.ccn}" style="display:inline-block;margin-top:6px;color:#34538F;font-weight:600;text-decoration:none">View hospital →</a>
        </div>`;
      dot.bindPopup(popup);
      dot.addTo(group);
    });
    group.addTo(mapRef.current);
    hospitalLayerRef.current = group;
  }, [hospitals, hospitalsOn]);

  // Connection lines (hospital ↔ SNF alignment lines)
  useEffect(() => {
    if (!mapRef.current) return;
    if (linksLayerRef.current) {
      mapRef.current.removeLayer(linksLayerRef.current);
      linksLayerRef.current = null;
    }
    if (!hospitalsOn || !hospitalLinks || !hospitalLinks.length) return;
    const group = L.layerGroup();
    hospitalLinks.forEach((lnk) => {
      if (!lnk.from || !lnk.to) return;
      const color = lnk.confidence === 'high' ? '#2D5F4F' : lnk.confidence === 'medium' ? '#A06628' : '#7A857F';
      const polyline = L.polyline([[lnk.from.lat, lnk.from.lng], [lnk.to.lat, lnk.to.lng]], {
        color,
        weight: lnk.confidence === 'high' ? 2.5 : 1.6,
        opacity: 0.7,
        dashArray: lnk.confidence === 'low' ? '4, 6' : null,
      });
      polyline.bindPopup(`
        <div style="font-family:Inter,sans-serif;font-size:12px;line-height:1.4;min-width:160px">
          <div style="font-weight:600;color:#1A2622">${(lnk.label || '').replace(/[<>]/g, '')}</div>
          <div style="margin-top:4px;color:#3A4A44">Confidence: <b style="color:${color}">${lnk.confidence}</b></div>
          ${lnk.distance_mi != null ? `<div style="color:#7A857F">${lnk.distance_mi} mi</div>` : ''}
          ${lnk.score != null ? `<div style="color:#7A857F">Alignment score: <b>${Math.round(lnk.score)}</b></div>` : ''}
        </div>`);
      polyline.addTo(group);
    });
    group.addTo(mapRef.current);
    linksLayerRef.current = group;
  }, [hospitalLinks, hospitalsOn]);

  // MA heatmap layer
  useEffect(() => {
    if (!mapRef.current) return;
    // Remove existing heat layer
    if (heatLayerRef.current) {
      mapRef.current.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
    }
    if (!heatmap || !heatmapData.length) return;

    let cancelled = false;
    (async () => {
      try {
        const geo = await loadUSStatesGeo();
        if (cancelled || !mapRef.current) return;
        const penByCode = {};
        heatmapData.forEach((s) => { penByCode[s.state_code] = s; });

        const layer = L.geoJSON(geo, {
          style: (feature) => {
            const code = STATE_NAME_TO_CODE[feature.properties.name];
            const s = code && penByCode[code];
            return {
              fillColor: s?.color || '#E5DCC4',
              weight: 1,
              opacity: 1,
              color: '#FBF6EC',
              fillOpacity: s ? 0.55 : 0.2,
            };
          },
          onEachFeature: (feature, layer2) => {
            const code = STATE_NAME_TO_CODE[feature.properties.name];
            const s = code && penByCode[code];
            const pct = s?.penetration_pct;
            const label = s?.label || 'No data';
            const html = `
              <div style="font-family:Inter,sans-serif;font-size:12px;line-height:1.4;min-width:160px">
                <div style="font-weight:600;color:#1A2622">${feature.properties.name}</div>
                <div style="margin-top:4px;color:#3A4A44">MA penetration: <b>${pct != null ? pct.toFixed(1) + '%' : '—'}</b></div>
                <div style="margin-top:4px;display:inline-block;padding:2px 6px;border-radius:4px;background:${(s?.color || '#9A9077')}33;color:${s?.color || '#9A9077'};font-weight:600;font-size:10.5px">${label}</div>
                ${code ? `<div style="margin-top:6px"><a href="/managed-care/state/${code}" style="color:#2D5F4F;font-weight:600;text-decoration:none">View MA market →</a></div>` : ''}
              </div>`;
            layer2.bindPopup(html);
          },
        });
        layer.addTo(mapRef.current);
        heatLayerRef.current = layer;
      } catch (e) { reportError('RealMap.heatmap', e); }
    })();

    return () => { cancelled = true; };
  }, [heatmap, heatmapData]);

  return (
    <div className="relative rounded-xl overflow-hidden border border-[#E5DCC4] bg-white" style={{ height }} data-testid="real-map">
      <div ref={containerRef} className="absolute inset-0"/>
      <div className="absolute top-3 right-3 z-[400] flex items-center gap-2">
        <div className="bg-white/95 backdrop-blur border border-[#E5DCC4] rounded-md px-3 py-1.5 text-[11px] text-[#3A4A44] shadow-soft">
          <span className="text-[#7A857F]">Markers:</span> <span className="font-semibold text-[#1A2622] ml-1">{markers.length}</span>
        </div>
        {showHospitalsToggle && (hospitals?.length > 0 || hospitalLinks?.length > 0) && (
          <button
            onClick={() => setHospitalsOn((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 h-7 rounded-md border text-[11px] font-semibold shadow-soft transition-colors ${hospitalsOn ? 'bg-[#34538F] text-white border-[#34538F]' : 'bg-white/95 text-[#34538F] border-[#5C82C0]'}`}
            data-testid="map-toggle-hospitals"
            title={hospitalsOn ? 'Hide hospitals' : 'Show hospitals'}
          >
            <Hospital size={11} />
            Hospitals {hospitalsOn ? 'on' : 'off'}
            {hospitals?.length ? <span className="ml-1 text-[10px] opacity-80">· {hospitals.length}</span> : null}
          </button>
        )}
      </div>
      {heatmap ? (
        <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur border border-[#E5DCC4] rounded-md px-3 py-2 text-[11px] shadow-soft flex items-center gap-2.5" data-testid="ma-legend">
          <span className="text-[#7A857F] uppercase tracking-wider text-[10px] font-semibold">MA Penetration</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background:'#4A7C5E'}}/>High opportunity</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background:'#D4A85C'}}/>Moderate</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background:'#D58668'}}/>Saturated</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background:'#C75D5D'}}/>Highly saturated</span>
        </div>
      ) : (
        <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur border border-[#E5DCC4] rounded-md px-3 py-2 text-[11px] shadow-soft flex items-center gap-2.5">
          <span className="text-[#7A857F] uppercase tracking-wider text-[10px] font-semibold">Rating</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background:'#2D5F4F'}}/>5</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background:'#4A7C5E'}}/>4</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background:'#D4A85C'}}/>3</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background:'#D58668'}}/>2</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background:'#C75D5D'}}/>1</span>
        </div>
      )}
    </div>
  );
}
